import BigNumber from 'bignumber.js';
import store from '../store';
import { EventBus } from '../api/eventbus';

export default class Shop {
    /**
     *
     */
    constructor({ id = 0, name = '', faClass = '', coolDown = 0, baseCosts = 1.0, individualCostFactor = 1, baseEarnings = 1.0, grade = 1 } = {}) {
        this.name = name;
        this.id = id;
        this.faClass = faClass;
        this.coolDown = coolDown;
        this._baseCosts = baseCosts; // the costs for the first branch without any upgrades
        this._individualCostFactor = individualCostFactor; // the shop's base factor to get costs per branch.
        // should be lower on better shop types and probably degrade over time and amount built branches
        this._baseEarnings = baseEarnings; // the earnings for the first single branch without any upgrades
        this.grade = grade;

        this.disableProduceButton = false;
        this.amount = 0;
        this.isProducing = false;
        this.wasProducing = false; // on the last save, the shop was currently producing.

        this.earningsMultiplier = 1.0; // the shop's earnings will be multiplied by this to support upgrades (should then be > 1)
        this.costMultiplier = 1.0; // the shop's building costs will be multiplied by this to support upgrades (should then be < 1)
        this.autoUpgrades = [
            { type: 'earnings', factor: 2, amount: 25 },
            { type: 'coolDown', factor: 2, amount: 50 },
            { type: 'earnings', factor: 2, amount: 100 },
            { type: 'coolDown', factor: 2, amount: 200 },
            { type: 'earnings', factor: 3, amount: 300 },
            { type: 'earnings', factor: 2, amount: 400 },
            { type: 'earnings', factor: 2, amount: 500 },
            { type: 'coolDown', factor: 2, amount: 750 },
            { type: 'earnings', factor: 2, amount: 1000 },
            { type: 'earnings', factor: 3, amount: 1500 },
            { type: 'earnings', factor: 2, amount: 3000 },
            { type: 'earnings', factor: 2, amount: 5000 },
            { type: 'earnings', factor: 2, amount: 10000 },
            { type: 'earnings', factor: 2, amount: 25000 },
            { type: 'earnings', factor: 2, amount: 50000 },
            { type: 'earnings', factor: 3, amount: 100000 },
            { type: 'earnings', factor: 3, amount: 250000 },
            { type: 'earnings', factor: 3, amount: 500000 },
            { type: 'earnings', factor: 3, amount: 1000000 }
        ]; // the updates at these levels; type can either be "coolDown" or "earnings"
        this.doProgressUpdates = true; // when set to false, the progress bar will be set to 100% and not be updated anymore
        this.individualEarningsFactor = 1; // the shop's base factor to get earnings. should be lower on better shop types and probably degrade over time and amount built branches
        this.internalFactor = 1; // used to combine multiple production cycles
        this.price = 1;
        this.icon = null; // the shop's image, located in images/
        this.productionProgress = 0; // the shop's current production progress in percent
        this.productionTimeRemaining = 0; // the time remaining until a good is produced
        this.hasManager = false; // if set to true, the player won't have to click to produce
        this.manager = false; // the manager object will be attached if the shop has one
        this.showEarningPerSecond = false; // could be used for upgrades - shows the income per second

        this.costsBigNumber = new BigNumber('0');
        // this.costsFormatted                = '';
        this.cachedCosts = [[], []]; // idx 0 is for formatted values, 1 for unformatted
        this._earnings = []; // unformatted values only
        this._earningsMultiplierWhenCached = 0;
        this.maxAmount = 1;
        this.costs = this.getCosts(this.amount, 1);
    }

    setEarningsMultiplier(multiplier) {
        this.earningsMultiplier = multiplier;
        // this.factory.calculateTotalIncomePerSecond();
        return this;
    }

    set individualCostFactor(individualCostFactor) {
        this._individualCostFactor = individualCostFactor;
        return this;
    }

    set baseEarnings(baseEarnings) {
        this._baseEarnings = baseEarnings;
        return this;
    }

    set baseCosts(baseCosts) {
        this._baseCosts = baseCosts;
        return this;
    }

    get individualCostFactor() {
        return this._individualCostFactor;
    }

    get baseEarnings() {
        return this._baseEarnings;
    }

    get baseCosts() {
        return this._baseCosts;
    }

    getCosts(currentAmount, amountToBuild, asBigNumber = false, formatted = false, cache = true) {
        let price = new BigNumber(0),
            i     = amountToBuild + currentAmount;
        if (typeof currentAmount !== 'number' || typeof amountToBuild !== 'number') {
            console.error('shop.getCosts() called with wrong param: ', currentAmount, amountToBuild, arguments);
            return asBigNumber ? new BigNumber(0) : Infinity;
        }
        if (typeof this.cachedCosts[currentAmount] !== 'undefined'
            && typeof this.cachedCosts[currentAmount][amountToBuild] !== 'undefined'
        ) {
            price = this.cachedCosts[currentAmount][amountToBuild];
        } else {
            for (i; i > currentAmount; --i) {
                price = price.plus(this.costFunction(i));
            }
        }
        if (cache === true) {
            if (typeof this.cachedCosts[currentAmount] === 'undefined') {
                this.cachedCosts[currentAmount] = [];
            }
            this.cachedCosts[currentAmount][amountToBuild] = price;
        }
        price = price.times(this.costMultiplier);
        return asBigNumber ? price : price.toNumber();
    }

    get earnings() {
        return new BigNumber(this.earningsMultiplier.toString()).times(this.earningsFunction(this.amount));
    }

    /**
     * default function to calculate a shop's earnings as a function of its current level
     * @param {int} level
     * @param {float} internalFactor
     * @param {boolean} asFloat
     * @returns {Number}
     */
    calculateEarnings(level, internalFactor = null, asFloat = false) {
        if (this._earningsMultiplierWhenCached !== this.earningsMultiplier) {
            // when upgrades change the multiplier, the cache has to be invalidated
            this._earnings = [];
        }
        // this is called at least at every tick - so the params don't change very often. try to cache the results.
        if ((internalFactor === null || internalFactor <= 1) && typeof this._earnings[level] !== 'undefined') {
            return asFloat ? parseFloat(this._earnings[level].toPrecision(12)) : this._earnings[level];
        }
        let earnings = new BigNumber(this.earningsMultiplier.toString()).times(this.earningsFunction(level));
        if (typeof level !== 'number') {
            console.error('shop.calculateEarnings() called with wrong param: ', arguments);
            return 0;
        }
        this._earningsMultiplierWhenCached = this.earningsMultiplier;
        if (internalFactor !== null && internalFactor > 1) {
            earnings = earnings.times(Math.floor(internalFactor * 1000) / 1000);
        } else {
            this._earnings = [];
            this._earnings[level] = earnings;
        }
        return asFloat ? parseFloat(earnings.toPrecision(12)) : earnings;
    }

    /**
     * @param seconds
     * @returns {BigNumber}
     */
    getIncomeForElapsedTime(seconds) {
        let calculatedIncome = new BigNumber('0'),
            fullCycles       = 0,
            firstCycle       = 0;
        if (this.hasManager && this.amount > 0) {
            if (this.productionTimeRemaining > seconds) {
                // the production cycle could not be completed in the mean time.
                this.productionTimeRemaining -= seconds;
                // offset is the production time minus the time since save
                this.produce({ offset: (this.coolDown - this.productionTimeRemaining) * 1000 });
            } else {
                if (seconds > this.productionTimeRemaining) {
                    firstCycle = 1;
                    seconds -= this.productionTimeRemaining;
                }
                fullCycles = Math.floor(seconds / this.coolDown);
                seconds -= fullCycles * this.coolDown;
                if (firstCycle === 1 && !seconds > 0) {
                    firstCycle = 0;
                }
                calculatedIncome = this.calculateEarnings(this.amount).times(firstCycle + fullCycles);
                this.produce({ offset: seconds * 1000 });
            }
        } else if (this.wasProducing) {
            if (seconds > this.productionTimeRemaining) {
                calculatedIncome = this.calculateEarnings(this.amount);
                this.productionTimeRemaining = this.coolDown;
            } else {
                this.produce({ remaining: this.productionTimeRemaining, goneForSeconds: seconds });
            }
        }
        this.wasProducing = false;
        return calculatedIncome;
    }

    /**
     * @todo: this will cause negative offsets if upgrades were bought that decrease production time
     * @param productionTimeOverride
     * @return {int}
     */
    produce(productionTimeOverride = false) {
        let productionTime,
            doUpdate,
            offset = 0;
        if (this.isProducing && productionTimeOverride === false) {
            return -1;
        }
        this.isProducing = true;
        this.productionTimeRemaining = productionTimeOverride.remaining ? productionTimeOverride.remaining : this.coolDown;
        productionTime = this.coolDown * 1000; // ms
        if (productionTimeOverride !== false && Object.prototype.hasOwnProperty.call(productionTimeOverride, 'remaining')) {
            offset = productionTime - productionTimeOverride.remaining * 1000 + productionTimeOverride.goneForSeconds * 1000;
        } else if (productionTimeOverride !== false && Object.prototype.hasOwnProperty.call(productionTimeOverride, 'offset')) {
            offset = productionTimeOverride.offset;
        }
        doUpdate = productionTime > 75;
        // try to avoid too short timeouts. combine them so that no balance update should occur in under 500ms
        if (this.hasManager && productionTime <= 500) {
            this.internalFactor = 1000 / productionTime;
            productionTime *= this.internalFactor;
        }

        this.disableProduceButton = true;
        if (this.doProgressUpdates === true && doUpdate === false && this.hasManager) {
            // the first time the production time is lower then minimum. set progress bar to 100%, add active class
            this.doProgressUpdates = false;
            this.productionProgress = 100;
        }
        return store.state.game.productionQueue.push({
            productionTime,
            doUpdate,
            shop:           this,
            finished:       false,
            start:          Date.now() - offset
        });
    }

    /**
     *
     * @param upgrades
     * @param fromMerge
     * @returns {{shop, upgradeIndex: *, upgrades: Array}}
     */
    applyAutoUpgrade(upgrades, fromMerge) {
        /* eslint-disable indent */
        const appliedUpgrades = [],
              allAchievements = store.getters.allAchievements;
        upgrades.forEach(upgrade => {
            if (Object.prototype.hasOwnProperty.call(upgrade, 'factor') && !fromMerge) {
                if (upgrade.type === 'coolDown') {
                    this.coolDown /= upgrade.factor;
                } else if (upgrade.type === 'earnings') {
                    this.earningsMultiplier *= upgrade.factor;
                } else {
                    console.error('Error: unknown upgrade type ', upgrade, ' for shop ', this);
                }
            }
            upgrade.shop = this.name;
            appliedUpgrades.push(upgrade);
            /* eslint-disable no-loop-func */
            allAchievements.forEach(achievement => {
                if (!achievement.isAchieved && achievement.shopID === this.id && achievement.branches <= upgrade.amount) {
                    store.commit('ACHIEVE', { achievement });
                }
            });
        });
        return { shop: this, upgrades: appliedUpgrades };
    }

    /**
     * @param loaded
     * @return {*}
     */
    merge(loaded) {
        this.manager = loaded.manager;
        this.coolDown = Math.floor(loaded.coolDown * 1000) / 1000;
        this.hasManager = loaded.hasManager;
        this.earningsMultiplier = loaded.earningsMultiplier;
        this.costMultiplier = loaded.costMultiplier;
        this.wasProducing = loaded.isProducing;

        this.productionTimeRemaining = loaded.productionTimeRemaining > 0
            ? loaded.productionTimeRemaining
            : loaded.coolDown;
        this.updateAmount(0, loaded.amount, true);
        this.costsBigNumber = this.getCosts(this.amount, this.maxAmount, true, false, true);
        this.costs = this.costsBigNumber.toNumber();
        // this.costsFormatted = NiceAmount(this.costsBigNumber);
    }

    /**
     *
     * @param {number}oldAmount
     * @param {number}newAmount
     * @param {boolean}fromMerge
     * @returns {*}
     */
    updateAmount(oldAmount, newAmount, fromMerge = false) {
        const upgrades = this.autoUpgrades.filter(up => up.amount <= newAmount && up.amount > oldAmount);
        this.amount = newAmount;
        return upgrades.length > 0 // we got to a level that is rewarded with a new upgrade!
            ? this.applyAutoUpgrade(upgrades, fromMerge)
            : true;
    }

    /**
     * @param {number}amountToBuild
     * @returns {Shop}
     */
    buildBranch(amountToBuild) {
        const costs = this.getCosts(this.amount, amountToBuild, true, false);
        store.dispatch('checkAchievements', { checkBalance: true });
        if (typeof amountToBuild !== 'number' || costs.gt(store.state.game.balance)) {
            return this;
        }
        store.commit('DECREASE_BALANCE', { amount: costs });
        if (this.id === 'LemonadeStand' && store.state.tutorial.step.id === 2) {
            store.commit('SET_STEP', 3);
        } else if (this.id === 'LemonadeStand' && store.state.tutorial.step.id === 4) {
            store.commit('SET_STEP', 5);
        } else if (this.id === 'PaperDelivery' && store.state.tutorial.step.id === 6) {
            store.commit('SET_STEP', 7);
        }
        EventBus.$emit('playSound', 'coins');
        // this.sounds.play('coins');
        let res = this.updateAmount(this.amount, this.amount + amountToBuild, false);
        if (store.state.game.buildIncrement === -1) {
            this.maxAmount = this.getMaxAffordableAmount(store.state.game.balance);
        }
        EventBus.$emit('builtBranch', this);
        if (res !== true && typeof res === 'object') {
            EventBus.$emit('addUpgradeMessage', res);
        }
        this.costsBigNumber = this.getCosts(this.amount, this.maxAmount, true, false);
        this.costs = this.costsBigNumber.toNumber();
        // this.costsFormatted = NiceAmount(this.costsBigNumber);
        // this.factory.totalIncomePerSecond = this.factory.calculateTotalIncomePerSecond();
        return this;
    }

    /**
     *
     * @param {number}level
     */
    costFunction(level) {
        return new BigNumber(this.individualCostFactor)
            .pow(level - 1)
            .times(this.baseCosts);
    }

    /**
     * calculate the earnings of given level. should always be more or less the same
     *
     * @param level
     */
    earningsFunction(level) {
        // less the same
        return new BigNumber(Math.pow(level, this.individualEarningsFactor))
            .times(this.baseEarnings);
    }

    /**
     * @param {BigNumber}balance
     * @returns {number}
     */
    getMaxAffordableAmount(balance) {
        let total = new BigNumber('0'),
            i     = 1;
        if (this.maxAmount > 99 && this.getCosts(this.amount, i, true, false) <= balance) {
            return this.maxAmount;
        }
        while (total.lte(balance)) {
            ++i;
            total = this.getCosts(this.amount, i, true, false);
            if (i > 100) {
                break;
            }
        }
        return i - 1;
    }

    calcMaxAmount(balance) {
        return new Promise((resolve, reject) => {
            let total = new BigNumber('0'),
                i     = 1;
            if (this.maxAmount > 99 && this.getCosts(this.amount, i, true, false) <= balance) {
                return this.maxAmount;
            }
            while (total.lte(balance)) {
                ++i;
                total = this.getCosts(this.amount, i, true, false);
                if (i > 100) {
                    break;
                }
            }
            resolve(i - 1);
        });
    }

    /**
     * @returns {String}
     */
    getIcon() {
        return this.icon !== null ? this.icon : `${this.id.toLowerCase()}.png`;
    }

    /**
     * @param {String} icon
     * @returns {Shop}
     */
    setIcon(icon = this.getIcon()) {
        this.icon = icon;
        return this;
    }

    isAffordable() {
        return this.costsBigNumber.lte(store.getters.totalBalance);
    }

    recalculateCosts() {
        this.costsBigNumber = this.getCosts(this.amount, this.maxAmount, true, false, true);
        this.costs = this.costsBigNumber.toString();
        // this.costsFormatted = NiceAmount(this.costsBigNumber);
    }
}
